import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'
import styled from '@emotion/styled'
import {
  EntityState,
  EntityType,
  EntityTypeEnum,
  MasterLegalAgreementType,
  OrganisationType,
  ReferenceDataEntityType,
  Status,
  TradingRelationship,
  WorkflowActions
} from 'api'
import { Groupbox, ModalFooter, ModalWindow } from 'view/components/panels'
import { DialogField, DialogRow } from 'view/components/inputs'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { HorizontalLayout } from 'view/styles/constants'
import { Locale, Translation } from 'locale'
import { TradingRelationshipTradingEntityCombobox } from 'view/shared/entity'
import { ToggleField } from 'view/components/toggle'
import { MasterLegalAgreementMultiComboBox } from 'view/shared/entity'
import { createReferenceDataEntityHook, useOnMount } from 'view/hooks'
import { ClonedComponentByShowModal, UnhappyModal } from 'view/shared/modal'
import { ValidationTooltip } from 'view/components/tooltip'

interface TradingEntityRelationshipsDialogProps extends ClonedComponentByShowModal {
  organisationType: OrganisationType
  organisationId: string
  tradingEntityId: string
  existingRelationshipsTradingEntityIds?: string[]
  workflowId: string
  entityId: string
}

const Content = styled(Groupbox)`
  min-width: 400px;
  min-height: 250px;
  max-height: calc(100vh - 300px);
  overflow-y: auto;
  padding: 0 20px;

  & > * {
    margin-bottom: 19px;
  }
`

const FiccTogglesTable = styled(`table`)`
  display: table;
  width: 100%;
  td {
    color: #404040;
    font-size: 12px;
    font-weight: 600;
  }
`

export const GridWrapper = styled.div<{ height: number }>`
  height: ${props => props.height}px;
  width: 100%;
`

const useReferenceDataTradingRelationship = createReferenceDataEntityHook(EntityType.TRADING_RELATIONSHIP)

export const TradingEntityRelationshipsDialog = (props: TradingEntityRelationshipsDialogProps) => {
  const tradingEntityRelationshipEntity = useReferenceDataTradingRelationship({ workflowId: props.workflowId, entityId: props.entityId })

  if (!tradingEntityRelationshipEntity) {
    return <UnhappyModal title="Trading Entity Relationship not found." />
  }

  return <TradingEntityRelationshipsDialogComponent {...props} tradingEntityRelationshipEntity={tradingEntityRelationshipEntity} />
}

const TradingEntityRelationshipsDialogComponent: React.FC<TradingEntityRelationshipsDialogProps & {
  tradingEntityRelationshipEntity: ReferenceDataEntityType<TradingRelationship>
}> = ({
  workflowId,
  entityId,
  onClose,
  tradingEntityRelationshipEntity,
  organisationType,
  organisationId,
  existingRelationshipsTradingEntityIds,
  tradingEntityId
}) => {
  const dispatch = useDispatch()

  const isNew = tradingEntityRelationshipEntity.entityState === EntityState.NEW

  const title = isNew ? Translation.TC_NEW_TRADING_ENTITY_RELATIONSHIP : Translation.TC_EDIT_TRADING_ENTITY_RELATIONSHIP

  const localTradingEntityRelationship: TradingRelationship = useMemo(
    () =>
      isNew
        ? {
            ...tradingEntityRelationshipEntity.data,
            ...(organisationType === OrganisationType.BUY_SIDE
              ? { buyTradingEntity: tradingEntityId }
              : { sellTradingEntity: tradingEntityId }),
            status: Status.APPROVED
          }
        : tradingEntityRelationshipEntity?.data,
    [isNew, organisationType, tradingEntityId, tradingEntityRelationshipEntity?.data]
  )

  const initialTradingEntityRelationship = useRef(localTradingEntityRelationship)

  const fieldName = organisationType === OrganisationType.BUY_SIDE ? 'sellTradingEntity' : 'buyTradingEntity'

  const handleUpdate = useCallback(
    (update: Partial<TradingRelationship>) => {
      dispatch(
        WorkflowActions.createEntityUpdateReferenceData<TradingRelationship>({
          entityId: localTradingEntityRelationship.id,
          entityType: EntityTypeEnum.TRADING_RELATIONSHIP,
          updatedEntity: {
            ...localTradingEntityRelationship,
            ...update
          },
          workflowId
        })
      )
    },
    [localTradingEntityRelationship, dispatch, workflowId]
  )

  const handleCancel = () => {
    if (tradingEntityRelationshipEntity.entityState === EntityState.NEW) {
      dispatch(WorkflowActions.createEntityDeleteReferenceData([{ entityId, entityType: EntityTypeEnum.TRADING_RELATIONSHIP }], workflowId))
    } else {
      dispatch(
        WorkflowActions.createEntityUpdateReferenceData({
          entityId: initialTradingEntityRelationship.current.id,
          entityType: EntityTypeEnum.TRADING_RELATIONSHIP,
          updatedEntity: initialTradingEntityRelationship.current,
          workflowId
        })
      )
    }

    if (onClose) {
      onClose()
    }
  }

  const handleTradingEntityUpdate = (responderTradingEntityId: string) =>
    handleUpdate({
      [fieldName]: responderTradingEntityId ?? undefined
    })

  const handleUsSponsoredClearingUpdate = (usSponsoredClearing: boolean) =>
    handleUpdate({
      usSponsoredClearing,
      ...(!usSponsoredClearing && { usSponsoredClearingByDefault: false })
    })

  const handleUsSponsoredClearingDefaultUpdate = (usSponsoredClearingByDefault: boolean) =>
    handleUpdate({
      usSponsoredClearingByDefault
    })

  const handleMasterLegalAgreementUpdate = (masterLegalAgreements: MasterLegalAgreementType[]) => {
    handleUpdate({
      masterLegalAgreements
    })
  }

  const handleDefaultAgreementUpdate = (masterLegalAgreement: MasterLegalAgreementType, checked: boolean) =>
    handleUpdate({
      defaultAgreement: checked ? masterLegalAgreement : null
    })

  const handleSave = () => {
    if (onClose) {
      onClose()
    }
  }

  useEffect(() => {
    const { defaultAgreement, masterLegalAgreements } = localTradingEntityRelationship
    if (masterLegalAgreements.length === 0) {
      handleUpdate({
        defaultAgreement: null
      })
      return
    }

    if (!defaultAgreement || !masterLegalAgreements.includes(defaultAgreement)) {
      handleUpdate({
        defaultAgreement: masterLegalAgreements[0]
      })
    }
  }, [handleUpdate, localTradingEntityRelationship])

  useOnMount(() => {
    handleUpdate(localTradingEntityRelationship)
  })

  return (
    <ModalWindow dataTestId={'TradingEntityRelationshipDialog-ModalWindow-Title'} title={Locale.translate(title)}>
      <Content>
        <DialogRow horizontalLayout={HorizontalLayout.Left}>
          <DialogField>
            <TradingRelationshipTradingEntityCombobox
              validationField={fieldName}
              validationIssues={tradingEntityRelationshipEntity.validationIssues ?? []}
              readOnly={!isNew}
              canClear={false}
              organisationId={organisationId}
              organisationType={organisationType === OrganisationType.BUY_SIDE ? OrganisationType.SELL_SIDE : OrganisationType.BUY_SIDE}
              existingRelationshipsTradingEntityIds={existingRelationshipsTradingEntityIds}
              value={
                organisationType === OrganisationType.BUY_SIDE
                  ? localTradingEntityRelationship.sellTradingEntity
                  : localTradingEntityRelationship.buyTradingEntity
              }
              onUpdate={handleTradingEntityUpdate}
            />
          </DialogField>
        </DialogRow>
        <DialogRow horizontalLayout={HorizontalLayout.Left}>
          <DialogField flex={0}>
            <ToggleField
              label={Locale.translate(Translation.TC_US_SPONSORED_CLEARING)}
              checked={localTradingEntityRelationship.usSponsoredClearing || false}
              onUpdate={handleUsSponsoredClearingUpdate}
              readOnly={false}
            />
          </DialogField>
          <DialogField flex={0}>
            <ToggleField
              fieldName="substitutionAllowed"
              label={Locale.translate(Translation.TC_US_SPONSORED_CLEARING_BY_DEFAULT)}
              checked={localTradingEntityRelationship.usSponsoredClearingByDefault || false}
              onUpdate={handleUsSponsoredClearingDefaultUpdate}
              readOnly={!localTradingEntityRelationship.usSponsoredClearing}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <MasterLegalAgreementMultiComboBox
              validationField={['masterLegalAgreements', 'defaultAgreement']}
              validationIssues={tradingEntityRelationshipEntity.validationIssues ?? []}
              value={localTradingEntityRelationship.masterLegalAgreements}
              defaultAgreement={localTradingEntityRelationship.defaultAgreement}
              onUpdate={handleMasterLegalAgreementUpdate}
              onUpdateDefaultAgreement={handleDefaultAgreementUpdate}
            />
          </DialogField>
        </DialogRow>
        {/*FICC Name short indicators */}
        <FiccTogglesTable>
          <tbody>
            <tr>
              <td>{Locale.translate(Translation.TC_INITIATOR_FICC_TO_INITIATOR_FIX)}</td>
              <td>
                <ToggleField
                  label={''}
                  checked={localTradingEntityRelationship.initiatorFiccToInitiatorFix || false}
                  onUpdate={(value: boolean) => handleUpdate({ initiatorFiccToInitiatorFix: value })}
                  readOnly={false}
                />
              </td>
            </tr>
            <br />
            <tr>
              <td>{Locale.translate(Translation.TC_INITIATOR_FICC_TO_RESPONDER_FIX)}</td>
              <td>
                <ToggleField
                  label={''}
                  checked={localTradingEntityRelationship.initiatorFiccToResponderFix || false}
                  onUpdate={(value: boolean) => handleUpdate({ initiatorFiccToResponderFix: value })}
                  readOnly={false}
                />
              </td>
            </tr>
            <br />
            <tr>
              <td>{Locale.translate(Translation.TC_RESPONDER_FICC_TO_INITIATOR_FIX)}</td>
              <td>
                <ToggleField
                  label={''}
                  checked={localTradingEntityRelationship.responderFiccToInitiatorFix || false}
                  onUpdate={(value: boolean) => handleUpdate({ responderFiccToInitiatorFix: value })}
                  readOnly={false}
                />
              </td>
            </tr>
            <br />
            <tr>
              <td>{Locale.translate(Translation.TC_RESPONDER_FICC_TO_RESPONDER_FIX)}</td>
              <td>
                <ToggleField
                  label={''}
                  checked={localTradingEntityRelationship.responderFiccToResponderFix || false}
                  onUpdate={(value: boolean) => handleUpdate({ responderFiccToResponderFix: value })}
                  readOnly={false}
                />
              </td>
            </tr>
            <br />
          </tbody>
        </FiccTogglesTable>
        {/*FICC Name short indicators */}
      </Content>
      <ModalFooter>
        <DialogRow horizontalLayout={HorizontalLayout.Right}>
          <DialogField flex={0}>
            <TextButton
              dataTestId={'TradingEntityRelationshipDialog-TextButton-ButtonCancel'}
              type={TextButtonType.Bad}
              onClick={handleCancel}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <ValidationTooltip validationIssues={tradingEntityRelationshipEntity.validationIssues ?? []}>
            <DialogField flex={0}>
              <TextButton
                dataTestId={'TradingEntityRelationshipDialog-TextButton-ButtonOk'}
                type={TextButtonType.Good}
                onClick={handleSave}
                disabled={
                  !!tradingEntityRelationshipEntity.validationIssues && tradingEntityRelationshipEntity.validationIssues.length > 0
                }>
                {Locale.translate(Translation.TC_BUTTON_OK)}
              </TextButton>
            </DialogField>
          </ValidationTooltip>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}
